import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import React from 'react';
import api, { ApiResult } from '../../../api/api-caller';
import AchievementIcon from '../../../assets/AchievementIcon';
import BusinessIcon from '../../../assets/BusinessIcon';
import ConnectionsIcon from '../../../assets/ConnectionsIcon';
import GearsIcon from '../../../assets/GearsIcon';
import MotivationIcon from '../../../assets/MotivationIcon';
import RelationsIcon from '../../../assets/RelationsIcon';
import TargetIcon from '../../../assets/TargetIcon';
import TrophyIcon from '../../../assets/TrophyIcon';
import { isDataImage } from '../../../utils/gui-utils';
import { isValidUrl } from '../../../utils/url-utils';

export interface NavItem {
  _id: number;
  title: string;
  enabled: boolean;
  description?: string;
  link: string;
  icon?: string | JSX.Element;
  children: NavItem[];
}

interface CacheData {
  [key: string]: any;
}

export interface IntermediateState {
  data: NavItem[];
  error: ErrorObject;
}

export interface NavItemsViewModelType {
  fetchNavItems(): Promise<NavItem[]>;
  getNavItems(path?: string[]): Promise<IntermediateState>;
  getId(): string;
}

const getNavitemsFromAPI = async (): Promise<NavItem[]> => {
  const call = {
    method: 'GET'
  };

  const result: ApiResult = await api.asyncCallApi('navitems', call);

  if (!result.ok) throw new Error(result.data.errorMessage);

  return result.data.children;
};

const icons: Record<string, JSX.Element> = {
  AchievementIcon: <AchievementIcon fontSize="inherit" />,
  BusinessIcon: <BusinessIcon fontSize="inherit" />,
  ConnectionsIcon: <ConnectionsIcon fontSize="inherit" />,
  GearsIcon: <GearsIcon fontSize="inherit" />,
  MotivationIcon: <MotivationIcon fontSize="inherit" />,
  RelationsIcon: <RelationsIcon fontSize="inherit" />,
  TargetIcon: <TargetIcon fontSize="inherit" />,
  TrophyIcon: <TrophyIcon fontSize="inherit" />
};

const changeIcon = (data: NavItem[]): NavItem[] => {
  const updateElement = (element: NavItem, index: number, arr: NavItem[]) => {
    //  do not change icon if it contains url or data image
    if (typeof element.icon === 'string' && (isDataImage(element.icon) || isValidUrl(element.icon)))
      return;

    arr[index] = {
      ...element,
      icon:
        typeof element.icon === 'string' && element.icon! in icons
          ? icons[element.icon as string]
          : icons.BusinessIcon
    };
    if (arr[index].children && arr[index].children.length > 0) {
      changeIcon(arr[index].children);
    }
  };
  data.forEach(updateElement);
  return data;
};

export const initialState: NavItem[] = [
  {
    _id: 0,
    title: '',
    description: '',
    enabled: true,
    link: '',
    children: []
  }
];

const viewModelCache: CacheData = {};
let loading: Promise<NavItem[]> | undefined;

const createNavItemsViewModel = (pageId: string = ''): NavItemsViewModelType => {
  let data: NavItem[];

  const getId = (): string => pageId;
  const fetchNavItems = async (): Promise<NavItem[]> => {
    const cacheKey: string = pageId || '';

    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await getNavitemsFromAPI(/* pageId */);

      data = changeIcon(data);
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getNavItems = async (path?: string[]): Promise<IntermediateState> => {
    if (!data) {
      if (!loading) {
        try {
          loading = fetchNavItems();
          data = await loading;
          loading = undefined;
        } catch (error) {
          loading = undefined;
          return {
            data: [],
            error: { message: 'Server unreachable', details: JSON.stringify(error) }
          };
        }
      } else {
        try {
          await loading;
          data = viewModelCache[pageId || ''];
        } catch (error) {
          return {
            data: [],
            error: { message: 'Server unreachable', details: JSON.stringify(error) }
          };
        }
      }
    }

    if (path) {
      const found = data.find(item => item.title.toLowerCase() === path[0].toLowerCase());
      const filteredData = found ? [found] : [];

      return { data: filteredData, error: { message: '', details: '' } };
    }

    return { data, error: { message: '', details: '' } };
  };

  return {
    getId,
    fetchNavItems,
    getNavItems
  };
};

export default createNavItemsViewModel;
